import React, { useMemo, useState } from 'react';
import styles from './style/User.module.css';
import { useLocation, useNavigate } from 'react-router-dom';

const smtpPorts = ['25', '465', '587', '2525'];

const User: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const tenant = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tenant')?.trim() || 'tenant_a';
  }, [location.search]);

  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    server_name: '',
    port_server_address: '',
    security: 'SSL/TLS',
    authentication_type: 'normale',
    username: '',
    password: '',
    generic_mail_address: '',
    notify_low_battery: false,
    notify_low_level: false,
    notify_disconnected: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (sendTestEmail: boolean) => {
    if (
      !formData.notify_low_battery &&
      !formData.notify_low_level &&
      !formData.notify_disconnected
    ) {
      alert('Seleziona almeno un tipo di notifica.');
      return;
    }

    setLoading(true);
    setEmailSent(false);

    try {
      const res = await fetch('/api/send_mail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Key': tenant,
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          save_credentials: true,
          send_test_email: sendTestEmail,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || data.message || 'Errore durante l’operazione');
        return;
      }

      alert(data.message || 'Operazione completata!');

      if (sendTestEmail) {
        setEmailSent(true);
      }
    } catch (err) {
      console.error('Errore richiesta /api/send_mail:', err);
      alert('Errore durante la richiesta al server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h2>Configurazione Invio Mail</h2>
          <p>Configura il server SMTP e scegli quali notifiche automatiche ricevere.</p>
        </div>

        <form className={styles.form}>
          <div className={styles.formGroup}>
            <label>Server Name</label>
            <input
              type="text"
              name="server_name"
              value={formData.server_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Port</label>
            <select
              name="port_server_address"
              value={formData.port_server_address}
              onChange={handleChange}
              required
            >
              <option value="">Seleziona una porta</option>
              {smtpPorts.map((port) => (
                <option key={port} value={port}>
                  {port}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Sicurezza</label>
            <select
              name="security"
              value={formData.security}
              onChange={handleChange}
              required
            >
              <option value="none">None</option>
              <option value="STARTTLS">STARTTLS</option>
              <option value="SSL/TLS">SSL/TLS</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Tipo Autenticazione</label>
            <select
              name="authentication_type"
              value={formData.authentication_type}
              onChange={handleChange}
              required
            >
              <option value="normale">Normale</option>
              <option value="criptata">Criptata</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Username</label>
            <input
              type="email"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Email Destinatario</label>
            <input
              type="email"
              name="generic_mail_address"
              value={formData.generic_mail_address}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Tipi di notifica</label>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  name="notify_low_battery"
                  checked={formData.notify_low_battery}
                  onChange={handleChange}
                />
                <span>Batteria Bassa</span>
              </label>

              <label className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  name="notify_low_level"
                  checked={formData.notify_low_level}
                  onChange={handleChange}
                />
                <span>Livello Basso</span>
              </label>

              <label className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  name="notify_disconnected"
                  checked={formData.notify_disconnected}
                  onChange={handleChange}
                />
                <span>Dispositivo Disconnesso</span>
              </label>
            </div>
          </div>

          <button
            type="button"
            className={styles.saveButton}
            onClick={() => handleSubmit(false)}
            disabled={loading}
          >
            {loading ? 'Attendere...' : 'Salva Configurazione'}
          </button>

          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(`/home?tenant=${encodeURIComponent(tenant)}`)}
            disabled={loading}
          >
            Back
          </button>

          {emailSent && (
            <p>Email di test inviata correttamente.</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default User;